// src/admin/AdminPendingClicks.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import api from '../utils/axiosInstance';
import { API } from '../config/apiConfig';
import AdminPageWrapper from './components/AdminPageWrapper';

function AdminPendingClicks() {
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [lastProcessResult, setLastProcessResult] = useState(null);
  const [error, setError] = useState(null);

  const loadDiagnostics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(API.adminPendingClicksDiagnose);
      setDiagnostics(response.data.diagnostics);
    } catch (err) {
      console.error('Error loading diagnostics:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load diagnostics');
    } finally {
      setLoading(false);
    }
  };

  const triggerProcessing = async () => {
    if (!window.confirm('Are you sure you want to manually trigger pending clicks processing?')) {
      return;
    }

    setProcessing(true);
    setError(null);
    setLastProcessResult(null);
    try {
      const response = await api.post(API.adminPendingClicksProcess);
      setLastProcessResult(response.data);
      // Reload diagnostics after processing
      setTimeout(() => {
        loadDiagnostics();
      }, 1000);
    } catch (err) {
      console.error('Error triggering processing:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process pending clicks');
      setLastProcessResult({
        success: false,
        error: err.response?.data?.error || err.message,
      });
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    loadDiagnostics();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadDiagnostics, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (enabled) => {
    return enabled ? 'success' : 'error';
  };

  const getStatusIcon = (enabled) => {
    return enabled ? <CheckCircleIcon /> : <ErrorIcon />;
  };

  return (
    <AdminPageWrapper title="Pending Clicks Management">
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Pending Clicks Processor
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadDiagnostics}
              disabled={loading}
              sx={{ mr: 2 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={processing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
              onClick={triggerProcessing}
              disabled={processing || loading}
            >
              {processing ? 'Processing...' : 'Process Now'}
            </Button>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Last Process Result */}
        {lastProcessResult && (
          <Alert
            severity={lastProcessResult.success ? 'success' : 'error'}
            sx={{ mb: 3 }}
            onClose={() => setLastProcessResult(null)}
          >
            {lastProcessResult.success ? (
              <>
                <strong>Processing completed successfully!</strong>
                <br />
                Records processed: {lastProcessResult.rowsAffected || 0}
                {lastProcessResult.duration && (
                  <> • Duration: {lastProcessResult.duration.toFixed(2)}s</>
                )}
              </>
            ) : (
              <>
                <strong>Processing failed:</strong> {lastProcessResult.error || lastProcessResult.message}
              </>
            )}
          </Alert>
        )}

        {loading && !diagnostics ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : diagnostics ? (
          <Grid container spacing={3}>
            {/* Job Configuration */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="primary" />
                    Job Configuration
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                      <Chip
                        icon={getStatusIcon(diagnostics.job.enabled)}
                        label={diagnostics.job.enabled ? 'Enabled' : 'Disabled'}
                        color={getStatusColor(diagnostics.job.enabled)}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Cron Schedule
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                        {diagnostics.job.cronSchedule}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Run on Startup
                      </Typography>
                      <Chip
                        label={diagnostics.job.runOnStartup ? 'Yes' : 'No'}
                        color={diagnostics.job.runOnStartup ? 'success' : 'default'}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Statistics */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="primary" />
                    Statistics
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total Unprocessed
                      </Typography>
                      <Typography variant="h4" color="primary" sx={{ fontWeight: 700 }}>
                        {diagnostics.statistics.totalUnprocessed || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Valid Clicks
                      </Typography>
                      <Typography variant="h4" color="success.main" sx={{ fontWeight: 700 }}>
                        {diagnostics.statistics.validClicks || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Invalid Tokens
                      </Typography>
                      <Typography variant="h5" color="error.main" sx={{ fontWeight: 700 }}>
                        {diagnostics.statistics.invalidTokens || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Invalid Country Codes
                      </Typography>
                      <Typography variant="h5" color="error.main" sx={{ fontWeight: 700 }}>
                        {diagnostics.statistics.invalidCountryCode || 0}
                      </Typography>
                    </Grid>
                  </Grid>
                  {diagnostics.statistics.oldestClick && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Oldest Unprocessed Click
                      </Typography>
                      <Typography variant="body2">
                        {new Date(diagnostics.statistics.oldestClick).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                  {diagnostics.statistics.newestClick && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Newest Unprocessed Click
                      </Typography>
                      <Typography variant="body2">
                        {new Date(diagnostics.statistics.newestClick).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Sample Clicks */}
            {diagnostics.sample && diagnostics.sample.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon color="primary" />
                      Sample Unprocessed Clicks (Latest 10)
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Click ID</TableCell>
                            <TableCell>User ID</TableCell>
                            <TableCell>Video ID</TableCell>
                            <TableCell>Ad ID</TableCell>
                            <TableCell>App ID</TableCell>
                            <TableCell>Country Code</TableCell>
                            <TableCell>Tokens</TableCell>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {diagnostics.sample.map((click) => (
                            <TableRow key={click.ClickID}>
                              <TableCell>{click.ClickID}</TableCell>
                              <TableCell>{click.UserID}</TableCell>
                              <TableCell>{click.VideoID || 'N/A'}</TableCell>
                              <TableCell>{click.AdID || 'N/A'}</TableCell>
                              <TableCell>{click.AppID}</TableCell>
                              <TableCell>{click.CountryCode}</TableCell>
                              <TableCell>
                                <Chip
                                  label={click.TokensEarned}
                                  color={click.TokensEarned > 0 ? 'success' : 'error'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {new Date(click.ClickTimestamp).toLocaleString()}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={click.ValidationStatus}
                                  color={
                                    click.ValidationStatus === 'Valid'
                                      ? 'success'
                                      : click.ValidationStatus.includes('Tokens')
                                      ? 'error'
                                      : 'warning'
                                  }
                                  size="small"
                                  icon={
                                    click.ValidationStatus === 'Valid' ? (
                                      <CheckCircleIcon />
                                    ) : (
                                      <WarningIcon />
                                    )
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Warnings */}
            {diagnostics.statistics.invalidTokens > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning" icon={<WarningIcon />}>
                  <strong>{diagnostics.statistics.invalidTokens} clicks</strong> have invalid tokens (TokensEarned
                  &lt;= 0). These will not be processed.
                </Alert>
              </Grid>
            )}
            {diagnostics.statistics.invalidCountryCode > 0 && (
              <Grid item xs={12}>
                <Alert severity="warning" icon={<WarningIcon />}>
                  <strong>{diagnostics.statistics.invalidCountryCode} clicks</strong> have invalid country codes (not
                  found in main.Country table). These will not be processed.
                </Alert>
              </Grid>
            )}
            {diagnostics.statistics.validClicks > 0 && diagnostics.statistics.validClicks === diagnostics.statistics.totalUnprocessed && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<InfoIcon />}>
                  <strong>{diagnostics.statistics.validClicks} valid clicks</strong> are ready to be processed. Click
                  "Process Now" to trigger processing.
                </Alert>
              </Grid>
            )}
          </Grid>
        ) : (
          <Alert severity="info">No diagnostics data available. Click "Refresh" to load.</Alert>
        )}
      </Container>
    </AdminPageWrapper>
  );
}

export default AdminPendingClicks;

